import React, { useCallback, useRef, useState } from 'react';

import {
  CheckCircleOutlined,
  UserOutlined,
  ShoppingOutlined,
  ArrowRightOutlined,
  EditOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  BankOutlined,
} from '@ant-design/icons';
import {
  formBridge,
  getCurrentLanguage,
  useAuth,
  useTheme,
} from '@fastlessjs/backoffice-layer/dist/hooks';
import debug from '@fastlessjs/backoffice-layer/dist/utils/debug';
import {
  Button,
  Modal,
  Typography,
  Card,
  Space,
  Progress,
  Divider,
  List,
  Badge,
} from 'antd';
import { AutoForm, AutoField } from 'uniforms-antd';

import { authorize } from '../../config/authorizations';
import { t } from '../../config/translations';

const { Text } = Typography;

/** Organization primary from theme (see global.less / useOrganizationTheme) */
const ORG_PRIMARY = 'var(--color-org-primary, var(--color-default-primary))';

type OrgHelpCircleButtonProps = {
  onOpenHelp: () => void;
  ariaLabel: string;
};

function OrgHelpCircleButton({ onOpenHelp, ariaLabel }: OrgHelpCircleButtonProps) {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onOpenHelp();
      }}
      style={{
        width: 22,
        height: 22,
        minWidth: 22,
        boxSizing: 'border-box',
        borderRadius: '50%',
        borderWidth: 2,
        borderStyle: 'solid',
        borderColor: ORG_PRIMARY,
        color: ORG_PRIMARY,
        background: 'transparent',
        padding: 0,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        verticalAlign: 'middle',
        fontSize: 11,
        fontWeight: 700,
        lineHeight: 1,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
      }}
    >
      ?
    </button>
  );
}

// Register this component for debug tracking
debug.registerComponent('SearchOrder');

export type FormData = { clientId?: string; orderId?: string };

export type SearchOrderProps = {
  onSubmit: (orderId: string, clientId?: string) => void;
};

// Client ID validation function
const validateClientId = (clientId: string): boolean => {
  // Validation - client ID should be numeric (with optional leading zero) like 123456 or 0123456
  // OR alphanumeric with dashes/underscores like O-0444012-B9B1
  const trimmed = clientId?.trim() || '';
  return Boolean(
    trimmed.length > 0 && (/^\d+$/.test(trimmed) || /^[A-Za-z0-9\-_]+$/.test(trimmed)),
  );
};

// Step 1: Client ID input schema
const clientIdSchema = () => ({
  title: t('schema.clientStepTitle'),
  type: 'object',
  properties: {
    clientId: {
      type: 'string',
      get title() {
        return t('searchOrder.clientNumber');
      },
      uniforms: {
        get help() {
          return t('searchOrder.clientNumberHelp');
        },
        placeholder: '123456',
      },
    },
  },
  required: ['clientId'],
});

// Step 2: Order ID input schema
const orderIdSchema = () => ({
  title: t('schema.orderStepTitle'),
  type: 'object',
  properties: {
    orderId: {
      type: 'string',
      get title() {
        return t('searchOrder.orderNumber');
      },
      uniforms: {
        get help() {
          return t('searchOrder.orderNumberHelp');
        },
        placeholder: '01218202',
      },
    },
  },
  required: ['orderId'],
});

const SearchOrder = (props: SearchOrderProps) => {
  debug.trackRender('SearchOrder');
  const { onSubmit } = props;
  const { user } = useAuth();
  debug.log('SearchOrder', `Rendering SearchOrder component`);

  // State for the stepped form (0 = conditions, 1 = client ID, 2 = order ID)
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<FormData>({});
  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  const [clientIdValid, setClientIdValid] = useState(false);
  const [, setConditionsAccepted] = useState(false);
  const [helpModalVisible, setHelpModalVisible] = useState(false);
  const { darkMode } = useTheme();

  const clientFormRef = useRef<any>(null);
  const orderFormRef = useRef<any>(null);

  // Handle client ID submission and validation
  const handleClientIdSubmit = useCallback(() => {
    clientFormRef.current?.submit();
  }, []);

  const handleClientIdChange = useCallback(
    (key: string, value: string) => {
      const forceEnterpriseClient =
        authorize(user?.group || '', 'search:order') && Boolean(user?.enterpriseId);
      const storedClientId = forceEnterpriseClient ? String(user.enterpriseId) : value;
      const idToValidate = forceEnterpriseClient ? String(user.enterpriseId) : value;

      setFormData((prev) => ({
        ...prev,
        [key]: value,
        clientId: storedClientId,
      }));
      setClientIdValid(validateClientId(idToValidate));
    },
    [user?.enterpriseId, user?.group],
  );

  /** Effective client id from form model (staff with search:order uses enterprise id). */
  const resolveClientIdFromModel = useCallback(
    (model: { clientId?: string }) => {
      const typed = typeof model?.clientId === 'string' ? model.clientId.trim() : '';
      const forceEnterprise =
        authorize(user?.group || '', 'search:order') && Boolean(user?.enterpriseId);
      return forceEnterprise ? String(user.enterpriseId).trim() : typed;
    },
    [user?.enterpriseId, user?.group],
  );

  // Handle moving to order ID step (uses validated form model — avoids stale React state)
  const handleClientStepComplete = useCallback(
    (model: { clientId?: string }) => {
      const resolved = resolveClientIdFromModel(model);
      if (!validateClientId(resolved) || !resolved) {
        debug.warn(
          'SearchOrder',
          `Client step blocked — invalid or empty client id after submit`,
        );
        return;
      }
      setFormData((prev) => ({ ...prev, clientId: resolved }));
      setClientIdValid(true);
      setCurrentStep(2);
    },
    [resolveClientIdFromModel],
  );

  // Handle accepting conditions
  const handleAcceptConditions = useCallback(() => {
    debug.log('SearchOrder', `Conditions accepted, moving to step 1`);
    setConditionsAccepted(true);
    setCurrentStep(1);
  }, []);

  // Handle order ID submission
  const handleOrderIdSubmit = useCallback(() => {
    orderFormRef.current?.submit();
  }, []);

  const handleOrderIdChange = useCallback((key: string, value: string) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  }, []);

  // Handle going back to client ID step
  const handlePrevStep = useCallback(() => {
    if (currentStep === 2) {
      setCurrentStep(1);
    } else if (currentStep === 1) {
      setCurrentStep(0);
    }
  }, [currentStep]);

  // Handle final submission (order id from validated form model; client id from latest state)
  const handleFinalSubmit = useCallback(
    (model: { orderId?: string }) => {
      const orderId = typeof model?.orderId === 'string' ? model.orderId.trim() : '';
      const { clientId } = formDataRef.current;
      if (!orderId || !clientId) {
        debug.warn(
          'SearchOrder',
          `Final submit blocked — orderId: ${Boolean(orderId)}, clientId: ${Boolean(
            clientId,
          )}`,
        );
        return;
      }
      debug.log(
        'SearchOrder',
        `Final submit - orderId: ${orderId}, clientId: ${clientId}`,
      );
      setFormData((prev) => ({ ...prev, orderId }));
      onSubmit(orderId, clientId);
    },
    [onSubmit],
  );

  const clientSchema = formBridge(clientIdSchema());
  const orderSchema = formBridge(orderIdSchema());

  // Calculate progress percentage (3 steps: conditions, client, order)
  const getProgressPercent = () => {
    if (currentStep === 0) return 33;
    if (currentStep === 1) return 66;
    return 100;
  };
  const progressPercent = getProgressPercent();

  return (
    <div
      style={{
        maxWidth: '800px',
        margin: '0 auto',
        padding: '24px',
        position: 'relative',
      }}
    >
      {/* Help Modal */}
      <Modal
        title={null}
        open={helpModalVisible}
        onCancel={() => setHelpModalVisible(false)}
        footer={null}
        width="95vw"
        style={{ maxWidth: '1400px', top: 20 }}
        bodyStyle={{
          padding: '24px',
          maxHeight: '90vh',
          overflow: 'auto',
          backgroundColor: '#f5f5f5',
        }}
        closeIcon={
          <div
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              backgroundColor: '#ff4d4f',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '24px',
              color: 'white',
              fontWeight: 'bold',
              cursor: 'pointer',
              marginTop: '8px',
              marginRight: '15px',
            }}
          >
            ×
          </div>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* Title with info cards above */}
          <div>
            <Typography.Title
              level={2}
              style={{
                margin: 0,
                color: '#1890ff',
                textAlign: 'center',
                marginBottom: '20px',
              }}
            >
              💡 {t('searchOrder.whereToFind')}
            </Typography.Title>

            {/* Info cards in title area - side by side, wider layout */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '24px',
                marginBottom: '8px',
              }}
            >
              {/* Client number */}
              <div
                style={{
                  backgroundColor: '#fffbe6',
                  padding: '12px 24px',
                  borderRadius: '10px',
                  border: '3px solid #faad14',
                  textAlign: 'center',
                  minWidth: '300px',
                  maxWidth: '400px',
                }}
              >
                <div style={{ fontSize: '28px', marginBottom: '6px' }}>📋</div>
                <Typography.Title
                  level={5}
                  style={{
                    margin: 0,
                    color: '#d48806',
                    marginBottom: '6px',
                    fontSize: '16px',
                  }}
                >
                  {t('searchOrder.clientNumber')}
                </Typography.Title>
                <Typography.Text
                  style={{ fontSize: '15px', display: 'block', marginBottom: '3px' }}
                >
                  {t('searchOrder.clientNumberExample')}{' '}
                  <Text code style={{ fontSize: '17px', fontWeight: 700 }}>
                    123456
                  </Text>
                </Typography.Text>
                <Typography.Text style={{ fontSize: '12px', color: '#8c8c8c' }}>
                  {t('searchOrder.topLeftInvoice')}
                </Typography.Text>
              </div>

              {/* Order number */}
              <div
                style={{
                  backgroundColor: '#e6f7ff',
                  padding: '12px 24px',
                  borderRadius: '10px',
                  border: '3px solid #1890ff',
                  textAlign: 'center',
                  minWidth: '300px',
                  maxWidth: '400px',
                }}
              >
                <div style={{ fontSize: '28px', marginBottom: '6px' }}>🛒</div>
                <Typography.Title
                  level={5}
                  style={{
                    margin: 0,
                    color: '#0050b3',
                    marginBottom: '6px',
                    fontSize: '16px',
                  }}
                >
                  {t('searchOrder.orderNumber')}
                </Typography.Title>
                <Typography.Text
                  style={{ fontSize: '15px', display: 'block', marginBottom: '3px' }}
                >
                  {t('searchOrder.clientNumberExample')}{' '}
                  <Text code style={{ fontSize: '17px', fontWeight: 700 }}>
                    01218202
                  </Text>
                </Typography.Text>
                <Typography.Text style={{ fontSize: '12px', color: '#8c8c8c' }}>
                  {t('searchOrder.orderColumn')}
                </Typography.Text>
              </div>
            </div>
          </div>

          {/* Visual example - Reduced size by 20% */}
          <div
            style={{
              textAlign: 'center',
              backgroundColor: 'white',
              borderRadius: '16px',
              padding: '24px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            }}
          >
            <img
              src={
                getCurrentLanguage() === 'fr'
                  ? '/aide_retour.jpg'
                  : '/your_order_number.jpg'
              }
              alt={t('img.returnHelp')}
              style={{
                width: '80%',
                maxWidth: '960px',
                height: 'auto',
                borderRadius: '12px',
                border: '4px solid #1890ff',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
              }}
            />
          </div>

          {/* Close button */}
          <div style={{ textAlign: 'center' }}>
            <Button
              type="primary"
              size="large"
              onClick={() => setHelpModalVisible(false)}
              style={{
                minWidth: '260px',
                height: '52px',
                fontSize: '18px',
                fontWeight: 600,
                borderRadius: '8px',
              }}
            >
              {t('searchOrder.understood')} 👍
            </Button>
          </div>
        </div>
      </Modal>

      {/* Header with title and help button */}
      <div style={{ position: 'relative', textAlign: 'center', marginBottom: '32px' }}>
        <Typography.Title
          level={3}
          style={{ margin: 0, color: '#1890ff', marginBottom: '8px' }}
        >
          🔍 {t('searchOrder.searchTitle')}
        </Typography.Title>
        <Typography.Text type="secondary" style={{ fontSize: '16px' }}>
          {t('searchOrder.searchSubtitle')}
        </Typography.Text>

        {/* Help Button positioned at the right */}
        <Button
          type="default"
          shape="circle"
          size="large"
          onClick={() => setHelpModalVisible(true)}
          style={{
            position: 'absolute',
            top: '0',
            right: '0',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            fontSize: '20px',
            width: '56px',
            height: '56px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography.Text style={{ fontSize: '24px', fontWeight: 'bold' }}>
            ?
          </Typography.Text>
        </Button>
      </div>

      {/* Progress indicator */}
      <div style={{ marginBottom: '24px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: '8px',
          }}
        >
          <Typography.Text strong>{t('searchOrder.progress')}</Typography.Text>
          <Typography.Text type="secondary">
            {currentStep + 1} / 3 {t('searchOrder.steps')}
          </Typography.Text>
        </div>
        <Progress
          percent={progressPercent}
          strokeColor={{
            '0%': '#fa8c16',
            '50%': '#108ee9',
            '100%': '#87d068',
          }}
          showInfo={false}
          size="small"
        />
      </div>

      {/* Step Cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        {/* Step 0: Conditions de retour */}
        <Card
          title={
            <Space>
              <Badge count="!" color="orange" style={{ fontSize: '18px' }} />
              <Typography.Text strong style={{ fontSize: '16px' }}>
                {t('searchOrder.step1Conditions')}
              </Typography.Text>
              {currentStep > 0 && (
                <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '18px' }} />
              )}
            </Space>
          }
          size="default"
          style={{
            borderWidth: currentStep === 0 ? '2px' : '1px',
            borderColor: currentStep === 0 ? '#fa8c16' : '#d9d9d9',
            boxShadow: currentStep === 0 ? '0 4px 12px rgba(250, 140, 22, 0.15)' : 'none',
            opacity: currentStep > 0 ? 0.8 : 1,
            backgroundColor: currentStep === 0 ? '#fffbe6' : undefined,
          }}
        >
          {currentStep === 0 ? (
            <>
              <Typography.Paragraph style={{ marginBottom: '24px', fontSize: '15px' }}>
                {t('searchOrder.conditionsIntro')}
              </Typography.Paragraph>

              <List
                itemLayout="horizontal"
                split
                dataSource={[
                  {
                    icon: (
                      <CheckCircleOutlined
                        style={{ fontSize: '22px', color: '#52c41a' }}
                      />
                    ),
                    title: t('searchOrder.itemState'),
                    content: t('searchOrder.itemStateContent'),
                  },
                  {
                    icon: (
                      <ClockCircleOutlined
                        style={{ fontSize: '22px', color: '#faad14' }}
                      />
                    ),
                    title: t('searchOrder.returnDelay'),
                    content: t('searchOrder.returnDelayContent'),
                  },
                  {
                    icon: (
                      <CloseCircleOutlined
                        style={{ fontSize: '22px', color: '#ff4d4f' }}
                      />
                    ),
                    title: t('searchOrder.excludedProducts'),
                    content: t('searchOrder.excludedProductsContent'),
                  },
                  {
                    icon: <BankOutlined style={{ fontSize: '22px', color: '#1890ff' }} />,
                    title: t('searchOrder.refund'),
                    content: t('searchOrder.refundContent'),
                  },
                ]}
                renderItem={(item: any) => (
                  <List.Item style={{ padding: '12px 0' }}>
                    <List.Item.Meta
                      avatar={<div style={{ marginRight: '12px' }}>{item.icon}</div>}
                      title={
                        <Text
                          strong
                          style={{
                            fontSize: '15px',
                            display: 'block',
                            marginBottom: '4px',
                          }}
                        >
                          {item.title}
                        </Text>
                      }
                      description={
                        <Text
                          style={{
                            fontSize: '14px',
                            lineHeight: '1.5',
                            color: '#595959',
                          }}
                        >
                          {item.content}
                        </Text>
                      }
                    />
                  </List.Item>
                )}
              />

              <Divider />

              <div style={{ textAlign: 'center' }}>
                <Button
                  type="primary"
                  size="large"
                  onClick={handleAcceptConditions}
                  style={{
                    minWidth: '250px',
                    backgroundColor: '#fa8c16',
                    borderColor: '#fa8c16',
                  }}
                >
                  {t('searchOrder.acceptConditions')}
                  <ArrowRightOutlined style={{ marginLeft: '8px' }} />
                </Button>
              </div>
            </>
          ) : (
            <Space>
              <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '18px' }} />
              <Typography.Text strong style={{ fontSize: '15px' }}>
                {t('searchOrder.conditionsAccepted')}
              </Typography.Text>
            </Space>
          )}
        </Card>

        {/* Step 1: Client ID */}
        <Card
          title={
            <Space>
              <UserOutlined
                style={{
                  fontSize: '18px',
                  color: currentStep >= 1 ? '#1890ff' : '#8c8c8c',
                }}
              />
              <Typography.Text strong style={{ fontSize: '16px' }}>
                {t('searchOrder.step2Client')}
              </Typography.Text>
              {currentStep > 1 && (
                <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '18px' }} />
              )}
            </Space>
          }
          size="default"
          style={{
            borderWidth: currentStep === 1 ? '2px' : '1px',
            borderColor: currentStep === 1 ? '#1890ff' : '#d9d9d9',
            boxShadow: currentStep === 1 ? '0 4px 12px rgba(24, 144, 255, 0.15)' : 'none',
            opacity: (() => {
              if (currentStep < 1) return 0.6;
              if (currentStep > 1) return 0.8;
              return 1;
            })(),
          }}
        >
          {/* eslint-disable-next-line no-nested-ternary */}
          {currentStep === 1 ? (
            <>
              <Typography.Paragraph type="secondary" style={{ marginBottom: '16px' }}>
                {t('searchOrder.enterClientId')}
              </Typography.Paragraph>

              <AutoForm
                schema={clientSchema}
                onSubmit={(model) => {
                  handleClientStepComplete(model as { clientId?: string });
                }}
                showInlineError
                errorsField={() => null}
                onChange={handleClientIdChange}
                ref={(ref) => {
                  clientFormRef.current = ref;
                }}
              >
                <AutoField
                  name="clientId"
                  label={
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                      }}
                    >
                      {t('searchOrder.clientNumber')}
                      <OrgHelpCircleButton
                        onOpenHelp={() => setHelpModalVisible(true)}
                        ariaLabel={t('searchOrder.whereToFind')}
                      />
                    </span>
                  }
                  help={
                    <Text style={{ color: darkMode ? 'white' : '' }}>
                      {t('searchOrder.clientNumberHelp')}
                    </Text>
                  }
                />
              </AutoForm>

              <Divider />

              <div style={{ textAlign: 'center' }}>
                <Space>
                  <Button
                    onClick={handlePrevStep}
                    icon={<ArrowRightOutlined style={{ transform: 'rotate(180deg)' }} />}
                  >
                    {t('searchOrder.backToConditions')}
                  </Button>
                  <Button
                    type="primary"
                    size="large"
                    onClick={handleClientIdSubmit}
                    disabled={!clientIdValid}
                    style={{ minWidth: '200px' }}
                  >
                    {t('searchOrder.validateAndContinue')}
                    {clientIdValid && (
                      <ArrowRightOutlined style={{ marginLeft: '8px' }} />
                    )}
                  </Button>
                </Space>
                {formData.clientId && !clientIdValid && (
                  <Typography.Text
                    type="danger"
                    style={{ display: 'block', marginTop: '8px' }}
                  >
                    {t('searchOrder.invalidClientFormat')}
                  </Typography.Text>
                )}
              </div>
            </>
          ) : // eslint-disable-next-line no-nested-ternary
          currentStep < 1 ? (
            <Typography.Text type="secondary">
              {t('searchOrder.acceptConditionsFirst')}
            </Typography.Text>
          ) : (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Space>
                <CheckCircleOutlined style={{ color: '#52c41a' }} />
                <Typography.Text strong>
                  {t('misc.client')}: {formData.clientId}
                </Typography.Text>
              </Space>
              <Button
                type="link"
                icon={<EditOutlined />}
                onClick={handlePrevStep}
                size="small"
              >
                {t('searchOrder.modify')}
              </Button>
            </div>
          )}
        </Card>

        {/* Step 2: Order ID */}
        <Card
          title={
            <Space>
              <ShoppingOutlined
                style={{
                  fontSize: '18px',
                  color: currentStep >= 2 ? '#1890ff' : '#8c8c8c',
                }}
              />
              <Typography.Text strong style={{ fontSize: '16px' }}>
                {t('searchOrder.step3Order')}
              </Typography.Text>
              {formData.orderId && (
                <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '18px' }} />
              )}
            </Space>
          }
          size="default"
          style={{
            borderWidth: currentStep === 2 ? '2px' : '1px',
            borderColor: currentStep === 2 ? '#1890ff' : '#d9d9d9',
            boxShadow: currentStep === 2 ? '0 4px 12px rgba(24, 144, 255, 0.15)' : 'none',
            opacity: currentStep < 2 ? 0.6 : 1,
          }}
        >
          {/* eslint-disable-next-line no-nested-ternary */}
          {currentStep === 2 ? (
            <>
              <Typography.Paragraph type="secondary" style={{ marginBottom: '16px' }}>
                {t('searchOrder.enterOrderId')}
              </Typography.Paragraph>

              <AutoForm
                schema={orderSchema}
                onSubmit={(model) => {
                  handleFinalSubmit(model as { orderId?: string });
                }}
                showInlineError
                errorsField={() => null}
                onChange={handleOrderIdChange}
                ref={(ref) => {
                  orderFormRef.current = ref;
                }}
              >
                <AutoField
                  name="orderId"
                  label={
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                      }}
                    >
                      {t('searchOrder.orderNumber')}
                      <OrgHelpCircleButton
                        onOpenHelp={() => setHelpModalVisible(true)}
                        ariaLabel={t('searchOrder.whereToFind')}
                      />
                    </span>
                  }
                  help={
                    <Text style={{ color: darkMode ? 'white' : '' }}>
                      {t('searchOrder.orderNumberHelp')}
                    </Text>
                  }
                />
              </AutoForm>

              <Divider />

              <div style={{ textAlign: 'center' }}>
                <Space direction="vertical" size="small">
                  <Button
                    onClick={handlePrevStep}
                    icon={<ArrowRightOutlined style={{ transform: 'rotate(180deg)' }} />}
                  >
                    {t('searchOrder.backToClient')}
                  </Button>
                  <Button
                    type="primary"
                    size="large"
                    onClick={handleOrderIdSubmit}
                    disabled={!formData.orderId}
                    style={{ minWidth: '200px' }}
                  >
                    🔍 {t('searchOrder.searchOrder')}
                  </Button>
                </Space>
              </div>
            </>
          ) : // eslint-disable-next-line no-nested-ternary
          currentStep < 2 ? (
            <Typography.Text type="secondary">
              {/* eslint-disable-next-line no-nested-ternary */}
              {currentStep === 0
                ? t('searchOrder.acceptConditionsFirst')
                : t('searchOrder.completeClientFirst')}
            </Typography.Text>
          ) : (
            <Typography.Text type="secondary">
              {t('searchOrder.orderNumberToEnter')}
            </Typography.Text>
          )}
        </Card>
      </div>
    </div>
  );
};

export default SearchOrder;

// Mark this component as initialized
debug.markInitialized('SearchOrder');
